/**
 * Generates a default conversation title based on the current timestamp
 * @returns A string in the format "Conversation - Month Day, Year"
 */
export const generateDefaultTitle = (): string => {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  };
  const dateStr = now.toLocaleDateString('en-US', options);
  return `Conversation - ${dateStr}`;
};

/**
 * Formats a timestamp into a relative time string (e.g., "2 hours ago", "Yesterday")
 * @param timestamp - ISO string or Date object
 * @returns A human-readable relative time string
 */
export const formatRelativeTime = (timestamp: string | Date): string => {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  }
};

/**
 * Truncates text to a specified length and adds ellipsis if needed
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if applicable
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)  }...`;
};

/**
 * Extracts a preview from message content, removing markdown and formatting
 * @param content - The raw message content
 * @param maxLength - Maximum length for the preview
 * @returns A clean text preview
 */
export const getMessagePreview = (content: string, maxLength: number = 100): string => {
  // Remove markdown formatting
  const preview = content
    .replace(/#{1,6}\s/g, '') // Remove headers
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1') // Remove bold/italic
    .replace(/`{1,3}[^`]*`{1,3}/g, '') // Remove code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '') // Remove images
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();

  return truncateText(preview, maxLength);
};